import type { Manifest } from "@prose-reader/shared"
import { webStreamer } from "../streamer/webStreamer"
import { STREAMER_URL_PREFIX } from "../../config"
import { skipToken, useQuery } from "@tanstack/react-query"
import { type Database, useDatabase } from "../../rxdb"
import { getMetadataFromBook } from "../../books/metadata"
import { httpClient } from "../../http/httpClient.shared"
import { getBookKoreaderDocument } from "../../integrations/koreader/client/getBookKoreaderDocument"

const getManifestBaseUrl = (origin: string, epubFileName: string) => {
  return `${origin}/${STREAMER_URL_PREFIX}/${epubFileName}/`
}

const loadManifest = async ({
  bookId,
  db,
}: {
  bookId: string
  db: Database
}) => {
  // A service worker only intercepts fetches for pages it controls, so its
  // streamer is usable exactly when it controls this page.
  const isSwStreamerAvailable = !!navigator.serviceWorker?.controller

  const { response: swStreamerResponse } = isSwStreamerAvailable
    ? await httpClient.fetch(
        `${window.location.origin}/streamer/${bookId}/manifest`,
        {},
      )
    : { response: undefined }

  const enrichManifest = async (_manifest: Manifest) => {
    const book = await db.book.findOne(bookId).exec()
    const metadata = getMetadataFromBook(book)

    return {
      ..._manifest,
      title: metadata.title?.toString() ?? _manifest.title,
    } satisfies Manifest
  }

  if (
    !swStreamerResponse ||
    swStreamerResponse.status === 415 ||
    /**
     * Most likely service worker is not registered.
     * Can happens on firefox during development
     */
    swStreamerResponse.headers.get("Content-Type") === "text/html"
  ) {
    const webStreamerResponse = await webStreamer.fetchManifest({
      key: bookId,
      baseUrl: getManifestBaseUrl(window.location.origin, bookId),
    })

    if (webStreamerResponse.status >= 400) {
      throw webStreamerResponse
    }

    return {
      manifest: await enrichManifest(await webStreamerResponse.json()),
      isUsingWebStreamer: true,
    }
  }

  const data: Manifest = await swStreamerResponse.json()

  return { manifest: await enrichManifest(data), isUsingWebStreamer: false }
}

export const useManifest = (bookId: string) => {
  const { db } = useDatabase()

  return useQuery({
    queryKey: ["reader/streamer/manifest", { bookId }],
    networkMode: "always",
    queryFn: db
      ? async function loadBook() {
          const [{ manifest, isUsingWebStreamer }, koreaderDocument] =
            await Promise.all([
              loadManifest({ bookId, db }),
              getBookKoreaderDocument(bookId),
            ])

          return { manifest, isUsingWebStreamer, koreaderDocument }
        }
      : skipToken,
    staleTime: Infinity,
    retry: (_, error) => !(error instanceof Response && error.status === 415),
  })
}
